import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { SheinShopProvider, useSheinShop } from '@/shein-platform/context/SheinShopContext';
import { SheinSyncJobProvider } from '@/shein-platform/context/SheinSyncJobContext';
import SheinShopTopBar from '@/shein-platform/layout/SheinShopTopBar';
import SheinGlobalSyncProgress from '@/shein-platform/components/SheinGlobalSyncProgress';
import SheinNoticeDrawerHost from '@/shein-platform/components/SheinNoticeDrawerHost';
import PlatformShopPickModal from '@/components/platform/PlatformShopPickModal';
import {
  IPE_SHEIN_BASE_PATH,
  SHEIN_NAV_ITEMS,
  SHEIN_SHOP_SWITCH_TOAST_KEY,
  buildSheinChildPath,
  ensureSheinWorkspaceTokenShared,
  openSheinPathInNewTab,
  parseSheinShopTypeSlugFromPath,
} from '@/shein-platform/constants/sheinRoutes';
import { sheinShopCapabilities } from '@/shein-platform/utils/sheinShopCapabilities';
import { isAllShopsScope, isConcreteShopId } from '@/utils/platformShopScope';
import { copyNavItem } from '@/i18n/bindCopy';
import { platformNavLabel } from '@/i18n/platformNavLabel';
import { showToast } from '@/utils/toast';
import '@/styles/shein-platform/shein-platform.css';

const NAV_EXPAND_STORAGE_KEY = 'ipe_shein_nav_expanded';

function appendShopIdQuery(path, shopId) {
  const base = String(path || '');
  if (!isConcreteShopId(shopId)) return base;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}shopId=${encodeURIComponent(String(shopId))}`;
}

function isNavGroupActive(pathname, parentPath) {
  const slug = parseSheinShopTypeSlugFromPath(pathname);
  const base = slug
    ? `${IPE_SHEIN_BASE_PATH}/${slug}/${parentPath}`
    : `${IPE_SHEIN_BASE_PATH}/${parentPath}`;
  return pathname === base || pathname.startsWith(`${base}/`);
}

function collectActiveGroupIds(pathname) {
  return SHEIN_NAV_ITEMS
    .filter((item) => Array.isArray(item.children) && item.children.length > 0)
    .filter((item) => isNavGroupActive(pathname, item.path))
    .map((item) => item.id);
}

function navItemsForShop(shop) {
  const caps = sheinShopCapabilities(shop);
  return SHEIN_NAV_ITEMS
    .filter((item) => {
      if (item.id === 'orders') {
        return caps.showOrdersNav || caps.showAfterSalesNav;
      }
      if (item.id === 'discuss' && !caps.showDiscussNav) return false;
      return true;
    })
    .map((item) => {
      if (item.id === 'orders' && Array.isArray(item.children)) {
        const children = item.children.filter((child) => {
          if (child.id === 'list') return caps.showOrdersNav;
          if (child.id === 'returns') return caps.showAfterSalesNav;
          return true;
        });
        return copyNavItem(item, { children });
      }
      if (item.id !== 'discuss' || !Array.isArray(item.children)) return item;
      // 自运营无成本议价单，仅保留建议零售价
      const children = caps.canDiscussCost
        ? item.children
        : item.children.filter((child) => child.id !== 'cost');
      return copyNavItem(item, { children });
    })
    .filter((item) => !Array.isArray(item.children) || item.children.length > 0);
}

function readStoredExpanded() {
  try {
    const raw = sessionStorage.getItem(NAV_EXPAND_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : null;
  } catch {
    return null;
  }
}

function writeStoredExpanded(ids) {
  try {
    sessionStorage.setItem(NAV_EXPAND_STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

function SheinSidebarNav({ expandedIds, toggleGroup, pathname }) {
  const { t } = useTranslation('workbench');
  const navText = (item) => platformNavLabel(t, item);
  const { activeShop, activeShopId, scopedShops = [], isAllShopsMode } = useSheinShop();
  const navItems = useMemo(() => navItemsForShop(activeShop), [activeShop]);
  const [pickOpen, setPickOpen] = useState(false);
  const [pendingPath, setPendingPath] = useState('');

  const openNewTabPath = (to) => {
    if (isAllShopsMode || isAllShopsScope(activeShopId)) {
      setPendingPath(to);
      setPickOpen(true);
      return;
    }
    const concrete = isConcreteShopId(activeShopId) ? activeShopId : '';
    openSheinPathInNewTab(appendShopIdQuery(to, concrete));
  };

  return (
    <>
    <nav className="shein-platform__sidebar" aria-label={t('platformNav.menuShein')}>
      {navItems.map((item) => {
        const hasChildren = Array.isArray(item.children) && item.children.length > 0;
        if (!hasChildren) {
          return (
            <NavLink
              key={item.id}
              to={item.path}
              className={({ isActive }) =>
                `shein-platform__nav-item${isActive ? ' shein-platform__nav-item--active' : ''}`
              }
            >
              {navText(item)}
            </NavLink>
          );
        }

        const routeActive = isNavGroupActive(pathname, item.path);
        const expanded = expandedIds.includes(item.id);
        const panelId = `shein-nav-panel-${item.id}`;

        return (
          <div
            key={item.id}
            className={`shein-platform__nav-group${expanded ? ' shein-platform__nav-group--open' : ''}${routeActive ? ' shein-platform__nav-group--active' : ''}`}
          >
            <button
              type="button"
              className={`shein-platform__nav-group-toggle${routeActive ? ' is-active' : ''}`}
              aria-expanded={expanded}
              aria-controls={panelId}
              onClick={() => toggleGroup(item.id)}
            >
              <span className="shein-platform__nav-group-label">{navText(item)}</span>
              <ChevronDown
                size={14}
                strokeWidth={2.25}
                className={`shein-platform__nav-group-chevron${expanded ? ' is-open' : ''}`}
                aria-hidden="true"
              />
            </button>
            <div
              id={panelId}
              className="shein-platform__nav-children"
              role="group"
              aria-label={t('platformNav.submenu', { label: navText(item) })}
              hidden={!expanded}
            >
              {item.children.map((child) => {
                const to = buildSheinChildPath(item.path, child.path);
                const className = 'shein-platform__nav-item shein-platform__nav-item--child';
                if (child.openInNewTab) {
                  return (
                    <a
                      key={child.id}
                      href={to}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={className}
                      onClick={(e) => {
                        e.preventDefault();
                        openNewTabPath(to);
                      }}
                    >
                      {navText(child)}
                    </a>
                  );
                }
                return (
                  <NavLink
                    key={child.id}
                    to={to}
                    end={child.path === '' || child.id === 'info'}
                    className={({ isActive }) =>
                      `${className}${isActive ? ' shein-platform__nav-item--active' : ''}`
                    }
                  >
                    {navText(child)}
                  </NavLink>
                );
              })}
            </div>
          </div>
        );
      })}
    </nav>
    <PlatformShopPickModal
      open={pickOpen}
      shops={scopedShops}
      title={t('platformBar.pickPublishShop')}
      description={t('platformBar.pickPublishShopDesc')}
      confirmText={t('platformBar.openPublishPage')}
      onClose={() => {
        setPickOpen(false);
        setPendingPath('');
      }}
      onConfirm={(shopId) => {
        setPickOpen(false);
        const path = pendingPath;
        setPendingPath('');
        if (path) openSheinPathInNewTab(appendShopIdQuery(path, shopId));
      }}
    />
    </>
  );
}

export default function SheinPlatformLayout() {
  const location = useLocation();
  const hideWorkspaceChrome = isNavGroupActive(location.pathname, 'products')
    || isNavGroupActive(location.pathname, 'compliance')
    || isNavGroupActive(location.pathname, 'templates')
    || isNavGroupActive(location.pathname, 'orders')
    || isNavGroupActive(location.pathname, 'after-sales')
    || isNavGroupActive(location.pathname, 'finance')
    || isNavGroupActive(location.pathname, 'shop')
    || isNavGroupActive(location.pathname, 'home')
    || isNavGroupActive(location.pathname, 'discuss')
    || isNavGroupActive(location.pathname, 'marketing')
    || location.pathname.startsWith(`${IPE_SHEIN_BASE_PATH}/certificates`);

  const activeGroupIds = useMemo(
    () => collectActiveGroupIds(location.pathname),
    [location.pathname],
  );

  const [expandedIds, setExpandedIds] = useState(() => {
    const stored = readStoredExpanded();
    const active = collectActiveGroupIds(
      typeof window !== 'undefined' ? window.location.pathname : '',
    );
    if (stored) {
      return Array.from(new Set([...stored, ...active]));
    }
    return active.length > 0
      ? active
      : SHEIN_NAV_ITEMS.filter((item) => item.children?.length).map((item) => item.id);
  });

  useEffect(() => {
    ensureSheinWorkspaceTokenShared();
  }, []);

  useEffect(() => {
    if (activeGroupIds.length === 0) return;
    setExpandedIds((prev) => {
      const next = Array.from(new Set([...prev, ...activeGroupIds]));
      if (next.length === prev.length && next.every((id) => prev.includes(id))) {
        return prev;
      }
      writeStoredExpanded(next);
      return next;
    });
  }, [activeGroupIds]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(SHEIN_SHOP_SWITCH_TOAST_KEY);
      if (!raw) return;
      sessionStorage.removeItem(SHEIN_SHOP_SWITCH_TOAST_KEY);
      const payload = JSON.parse(raw);
      if (payload?.message) {
        showToast(payload.message, payload.type || 'success');
      }
    } catch {
      sessionStorage.removeItem(SHEIN_SHOP_SWITCH_TOAST_KEY);
    }
  }, []);

  const toggleGroup = (id) => {
    setExpandedIds((prev) => {
      const next = prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id];
      writeStoredExpanded(next);
      return next;
    });
  };

  return (
    <SheinShopProvider>
      <SheinSyncJobProvider>
        <div className={`shein-platform${hideWorkspaceChrome ? ' shein-platform--product-ops' : ''}`} data-testid="shein-platform-layout">
          <SheinShopTopBar />
          <SheinNoticeDrawerHost />
          <div className="shein-platform__body">
            <SheinSidebarNav
              expandedIds={expandedIds}
              toggleGroup={toggleGroup}
              pathname={location.pathname}
            />
            <main className={`shein-platform__main${hideWorkspaceChrome ? ' shein-platform__main--product-ops' : ''}`}>
              <SheinGlobalSyncProgress />
              <Outlet />
            </main>
          </div>
        </div>
      </SheinSyncJobProvider>
    </SheinShopProvider>
  );
}
